import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { buildThreadTree, ThreadedMessage, parseThreadMetadata, addThreadMetadata } from '../lib/threadUtils';
import { useCommunity } from './useCommunity';

// Selection for messages including profile and comments data to match Community flat view
const selection = `
  *,
  profiles:user_id (
    email,
    display_name,
    avatar_url,
    is_admin
  ),
  comments:comments (
    id,
    content,
    created_at,
    profiles:user_id (
      email,
      display_name,
      avatar_url
    )
  )
`;

export function useThreads(initialParentId?: string | null) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [threadedMessages, setThreadedMessages] = useState<ThreadedMessage[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(initialParentId || null);
  
  const { 
    toggleLike, 
    deleteMessage, 
    currentUser
  } = useCommunity();
  
  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch messages using the existing endpoint
      const { data, error } = await supabase
        .from('messages')
        .select(selection)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      // Build thread tree from flat messages
      const threaded = buildThreadTree(data || []);
      setThreadedMessages(threaded);
    } catch (err: any) {
      console.error('Error fetching threads:', err);
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchThread = useCallback(async (threadId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate that the thread ID exists
      const { error: rootError } = await supabase
        .from('messages')
        .select('id')
        .eq('id', threadId)
        .single();
        
      if (rootError) throw rootError;
      
      // Fetch potential replies - using content LIKE as a workaround
      // In a real implementation, this would be optimized
      const { data: relatedMessages, error: relatedError } = await supabase
        .from('messages')
        .select(selection)
        .or(`content.like.%[reply:${threadId}]%,id.eq.${threadId}`)
        .order('created_at', { ascending: true });
        
      if (relatedError) throw relatedError;
      
      // Build thread tree
      const thread = buildThreadTree(relatedMessages || []);
      setThreadedMessages(thread);
      setSelectedThreadId(threadId);
    } catch (err: any) {
      console.error('Error fetching thread:', err);
      setError(err.message || 'Failed to load thread');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const createReply = useCallback(async (content: string, parentId: string) => {
    if (!currentUser) {
      throw new Error('You must be logged in to reply');
    }
    
    try {
      // Get parent message to properly set up ancestors
      const { data: parentMessage, error: parentError } = await supabase
        .from('messages')
        .select('*')
        .eq('id', parentId)
        .single();
        
      if (parentError) throw parentError;
      
      // Parse parent's thread metadata
      const threadedParent = parseThreadMetadata(parentMessage);
      
      // Create ancestors array - parent's ancestors plus parent itself
      const ancestors = [...threadedParent.ancestors, parentId];
      
      // Add thread metadata to content
      const threadedContent = addThreadMetadata(content, parentId, ancestors);
      
      // Insert using existing messages table
      const { data: newMessage, error: insertError } = await supabase
        .from('messages')
        .insert([{
          content: threadedContent,
          user_id: currentUser.id,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();
        
      if (insertError) throw insertError;
      
      // Refresh the messages
      if (selectedThreadId) {
        await fetchThread(selectedThreadId);
      } else {
        await fetchMessages();
      }
      
      return parseThreadMetadata(newMessage);
    } catch (err: any) {
      console.error('Error creating reply:', err);
      throw err;
    }
  }, [currentUser, fetchMessages, fetchThread, selectedThreadId]);
  
  const editMessage = useCallback(async (messageId: string, content: string) => {
    if (!currentUser) {
      throw new Error('You must be logged in to edit a message');
    }
    
    try {
      // Get the message to preserve thread metadata
      const { data: existingMessage, error: fetchError } = await supabase
        .from('messages')
        .select('*')
        .eq('id', messageId)
        .single();
        
      if (fetchError) throw fetchError;
      
      // Parse existing thread metadata
      const threadedMessage = parseThreadMetadata(existingMessage);
      
      // Preserve threading metadata
      const updatedContent = addThreadMetadata(
        content,
        threadedMessage.parent_id || null,
        threadedMessage.ancestors
      );
      
      // Update the message
      const { error: updateError } = await supabase
        .from('messages')
        .update({ content: updatedContent })
        .eq('id', messageId);
        
      if (updateError) throw updateError;
      
      // Refresh the messages
      if (selectedThreadId) {
        await fetchThread(selectedThreadId);
      } else {
        await fetchMessages();
      }
    } catch (err: any) {
      console.error('Error editing message:', err);
      throw err;
    }
  }, [currentUser, fetchMessages, fetchThread, selectedThreadId]);
  
  const handleToggleLike = useCallback(async (messageId: string) => {
    try {
      await toggleLike(messageId);
      
      // Refresh messages
      if (selectedThreadId) {
        await fetchThread(selectedThreadId);
      } else {
        await fetchMessages();
      }
    } catch (err: any) {
      console.error('Error toggling like:', err);
      setError(err.message || 'Failed to like message');
    }
  }, [toggleLike, fetchMessages, fetchThread, selectedThreadId]);
  
  const handleDeleteMessage = useCallback(async (messageId: string) => {
    try {
      // Get message info to pass user_id to the deleteMessage function
      const { data: messageToDelete } = await supabase
        .from('messages')
        .select('user_id')
        .eq('id', messageId)
        .single();
      
      if (messageToDelete) {
        await deleteMessage(messageId, messageToDelete.user_id);
      }
      
      // Refresh messages
      if (selectedThreadId) {
        await fetchThread(selectedThreadId);
      } else {
        await fetchMessages();
      }
    } catch (err: any) {
      console.error('Error deleting message:', err);
      setError(err.message || 'Failed to delete message');
    }
  }, [deleteMessage, fetchMessages, fetchThread, selectedThreadId]);
  
  // Initial load
  useEffect(() => {
    if (initialParentId) {
      fetchThread(initialParentId);
    } else {
      fetchMessages();
    }
  }, [initialParentId, fetchThread, fetchMessages]);

  // Realtime updates for the currently selected thread
  useEffect(() => {
    if (!selectedThreadId) return;

    const channel = supabase
      .channel(`thread-${selectedThreadId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        async (payload: any) => {
          try {
            const meta = parseThreadMetadata(payload.new);
            if (
              meta.parent_id === selectedThreadId ||
              (Array.isArray(meta.ancestors) && meta.ancestors.includes(selectedThreadId)) ||
              payload.new.id === selectedThreadId
            ) {
              await fetchThread(selectedThreadId);
            }
          } catch {
            // Fallback: refresh the thread on any insert if parsing fails
            await fetchThread(selectedThreadId);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages' },
        async (payload: any) => {
          // If the updated message is the root or belongs to the thread, refresh
          try {
            const meta = parseThreadMetadata(payload.new);
            if (
              payload.new.id === selectedThreadId ||
              meta.parent_id === selectedThreadId ||
              (Array.isArray(meta.ancestors) && meta.ancestors.includes(selectedThreadId))
            ) {
              await fetchThread(selectedThreadId);
            }
          } catch {
            await fetchThread(selectedThreadId);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'messages' },
        async (_payload: any) => {
          // A message removal might affect the thread tree
          await fetchThread(selectedThreadId);
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'comments' },
        async (_payload: any) => {
          // Comment added to any message in the thread
          await fetchThread(selectedThreadId);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'comments' },
        async (_payload: any) => {
          await fetchThread(selectedThreadId);
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'comments' },
        async (_payload: any) => {
          await fetchThread(selectedThreadId);
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [selectedThreadId, fetchThread]);
  
  return {
    loading,
    error,
    threadedMessages,
    selectedThreadId,
    setSelectedThreadId,
    refreshMessages: fetchMessages,
    fetchThread,
    createReply,
    editMessage,
    toggleLike: handleToggleLike,
    deleteMessage: handleDeleteMessage,
  };
}
